import { useState } from "react";
import { ShieldCheck, Eye, LogIn } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

import { LOGO } from "@/lib/kop";

const HERO = "/login-bg-2.jpg";

const ROLES = [
  { id: "admin", label: "Admin Kelurahan", icon: ShieldCheck, desc: "Input, ubah & mutasi data warga" },
  { id: "lurah", label: "Lurah", icon: Eye, desc: "Melihat data & laporan" },
];

export default function Login() {
  const { login } = useAuth();
  const [role, setRole] = useState("admin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(email, password);
      if (u.role !== role) toast.info(`Anda masuk sebagai ${u.role === "admin" ? "Admin Kelurahan" : "Lurah"}`);
      else toast.success(`Selamat datang, ${u.name}`);
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden" data-testid="login-page">
      <img src={HERO} alt="Bundaran Mimika Smart City" className="absolute inset-0 h-full w-full object-cover scale-105" data-testid="login-bg" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#041f1d]/95 via-[#072f2c]/80 to-[#072f2c]/40" />
      <div className="papua-trim relative" />
      <div className="relative min-h-[calc(100vh-6px)] grid lg:grid-cols-[1.15fr_1fr] gap-10 px-6 sm:px-12 lg:px-20 py-10 items-center">
        <div className="text-white fade-up">
          <div className="flex items-center gap-4">
            <img src={LOGO} alt="Logo Kabupaten Mimika" className="h-24 sm:h-28 w-auto drop-shadow-2xl" data-testid="login-logo" />
            <div className="text-sm leading-tight text-emerald-50/85">
              <div className="font-semibold tracking-widest uppercase text-amber-400 text-xs">Pemerintah Kabupaten Mimika</div>
              <div className="mt-1">Distrik Wania</div>
              <div className="font-semibold text-white">Kelurahan Inauga</div>
            </div>
          </div>
          <h1 className="mt-8 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight">TAPARU INAUGA</h1>
          <p className="mt-3 text-lg sm:text-xl font-semibold text-amber-400" data-testid="login-tagline">Tata Administrasi Penduduk Akurat & Rekapitulasi Umum Inauga</p>
          <p className="mt-4 max-w-lg text-emerald-50/85 leading-relaxed">
            Sistem Informasi Pendataan Penduduk & Layanan Surat Kependudukan Kelurahan Inauga — 16 RT, OAP & Non-OAP, kelompok usia, status ekonomi, dan arsip surat dalam satu sistem.
          </p>
          <div className="hidden sm:flex gap-8 mt-10 text-sm">
            {[["16", "Rukun Tetangga"], ["19", "Jenis Surat"], ["2026", "Standar Dukcapil"]].map(([a, b]) => (
              <div key={b}><div className="font-display text-3xl font-bold text-amber-400">{a}</div><div className="text-emerald-50/70">{b}</div></div>
            ))}
          </div>
        </div>
        <form onSubmit={submit} className="w-full max-w-md lg:justify-self-end bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl p-7 sm:p-9 fade-up" style={{ animationDelay: "120ms" }} data-testid="login-form">
          <p className="text-xs font-semibold tracking-widest uppercase text-amber-700">Masuk Sistem</p>
          <h2 className="text-2xl font-bold mt-1 text-slate-900">Selamat datang</h2>
          <p className="text-sm text-slate-500 mt-1">Pilih jenis akun lalu masukkan email & kata sandi.</p>
          <div className="grid grid-cols-2 gap-3 mt-5">
            {ROLES.map((r) => (
              <button
                type="button"
                key={r.id}
                onClick={() => setRole(r.id)}
                data-testid={`login-role-${r.id}`}
                className={`text-left rounded-xl border p-3.5 transition-colors duration-200 ${
                  role === r.id ? "border-[#0b4f4a] bg-[#0b4f4a] text-white" : "bg-white hover:border-slate-400"
                }`}
              >
                <r.icon className={`h-5 w-5 ${role === r.id ? "text-amber-400" : "text-[#0b4f4a]"}`} />
                <div className="font-semibold text-sm mt-2">{r.label}</div>
                <div className={`text-[11px] mt-0.5 ${role === r.id ? "text-emerald-50/75" : "text-slate-500"}`}>{r.desc}</div>
              </button>
            ))}
          </div>
          <div className="space-y-4 mt-6">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5 h-11 bg-white" data-testid="login-email-input" />
            </div>
            <div>
              <Label htmlFor="password">Kata Sandi</Label>
              <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1.5 h-11 bg-white" data-testid="login-password-input" />
            </div>
            {error && <div className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2" data-testid="login-error">{error}</div>}
            <Button type="submit" disabled={loading} className="w-full h-11 bg-[#0b4f4a] hover:bg-[#072f2c]" data-testid="login-submit-button">
              <LogIn className="h-4 w-4 mr-2" /> {loading ? "Memproses…" : `Masuk sebagai ${role === "admin" ? "Admin" : "Lurah"}`}
            </Button>
          </div>
          <p className="text-[11px] text-slate-500 mt-6 text-center">© {new Date().getFullYear()} Kelurahan Inauga · Akun dibuat oleh pihak kelurahan</p>
        </form>
      </div>
    </div>
  );
}
